import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import React from 'react';
import {Field, useForm} from '@tanstack/react-form';
import {useMutation} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {fetch} from '../../shared/api/v1';
import type {components} from '../../shared/api/v1/openapi.types';
import {useAuth} from '../../providers/auth/auth-provider';
import {useMessage} from '../../providers/message/message-provider';
import {RoutesEnum} from '../../constants/routes';
import {emailFormat, minLength, required} from '../../utils/validation';

type RegisterDto = components['schemas']['RegisterDto'];
type AuthResponseDto = components['schemas']['AuthResponseDto'];

type FormValues = RegisterDto & {
  confirmPassword: string;
};

const noSpaces = (value?: string) =>
  value && /\s/.test(value) ? 'Пробелы недопустимы' : undefined;

export const SignUpForm = () => {
  const navigate = useNavigate();
  const {storeAuth, closeSignUpModal, openSignInModal} = useAuth();
  const {showError, showMessage} = useMessage();
  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const registerMutation = useMutation<AuthResponseDto, Error, RegisterDto>({
    mutationFn: async (data: RegisterDto) => {
      const response = await fetch.POST('/auth/register', {
        body: data,
      });

      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Ошибка при регистрации');
      }

      if (!response.data) {
        throw new Error('Ошибка при регистрации. Попробуйте позже.');
      }

      return response.data;
    },
    onSuccess: (data) => {
      storeAuth(data);
      closeSignUpModal();
      showMessage({
        message: 'Регистрация прошла успешно',
      });
      navigate(`/${RoutesEnum.CABINET}`);
    },
    onError: (error) => {
      showError({
        message: error.message || 'Ошибка при регистрации. Проверьте данные.',
      });
    },
  });

  const form = useForm({
    defaultValues: {
      email: '',
      login: '',
      name: '',
      password: '',
      confirmPassword: '',
    } as FormValues,
    onSubmit: async ({value}) => {
      try {
        const payload: RegisterDto = {
          email: value.email,
          login: value.login,
          name: value.name,
          password: value.password,
        };
        await registerMutation.mutateAsync(payload);
      } catch {
        // Errors are handled in onError
      }
    },
  });
  const Subscribe = form.Subscribe;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-column gap-2">
        <Field
          form={form}
          name="login"
          validators={{
            onChange: ({value}) =>
              required(value) ?? minLength(value, 3) ?? noSpaces(value),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Логин</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="email"
          validators={{
            onChange: ({value}) => required(value) ?? emailFormat(value),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Email</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="email"
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="name"
          validators={{
            onChange: ({value}) => required(value) ?? minLength(value, 3),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Имя</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="password"
          validators={{
            onChange: ({value}) => required(value) ?? minLength(value, 6),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Пароль</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="password"
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="confirmPassword"
          validators={{
            onChange: ({value, fieldApi}) => {
              if (!value?.trim()) {
                return required(value);
              }
              const password = fieldApi.form.state.values.password;
              return value === password ? undefined : 'Пароли не совпадают';
            },
            onChangeListenTo: ['password'],
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Повторите пароль</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="password"
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <div className="mt-2" />
        <Subscribe<[boolean, boolean]>
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <div className="flex flex-column gap-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                loading={isSubmitting}
              >
                Зарегистрироваться
              </Button>
              <Button
                type="button"
                text
                onClick={() => {
                  closeSignUpModal();
                  openSignInModal();
                }}
                loading={isSubmitting}
              >
                Уже есть аккаунт? Войти
              </Button>
            </div>
          )}
        </Subscribe>
      </div>
    </form>
  );
}
